import React, { useEffect, useRef, useState } from 'react';
import { FileDown, Loader2, Save, Send, Trash2 } from 'lucide-react';
import { Candidate } from '../api/candidate';
import {
  EmailBatch, EmailDraft, EmailPreview, ThankYouTemplate,
  getRecentThankYouBatches, getThankYouBatch, getThankYouTemplates,
  previewThankYouEmail, saveThankYouTemplate, sendThankYouEmail, deleteThankYouTemplate,
} from '../api/candidateEmail';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { SearchableSelect } from './ui/searchable-select';
import { useToast } from './ui/toast';

const statusLabels: Record<string, string> = {
  queued: '待发送', sending: '发送中', sent: '已发送', failed: '发送失败', skipped: '已跳过', unknown: '结果待核实',
};

function errorMessage(error: any): string {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : '操作失败，请稍后重试';
}

function requestId(): string {
  // 同一确认动作重试时复用此 ID，避免网络超时后重复投递。
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function CandidateThankYouDialog({ candidates, onClose }: { candidates: Candidate[]; onClose: () => void }) {
  const { showToast } = useToast();
  const [step, setStep] = useState<'edit' | 'preview' | 'result'>('edit');
  const [recipientIds, setRecipientIds] = useState(candidates.map(row => String(row.id)));
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [resend, setResend] = useState(false);
  const [templates, setTemplates] = useState<ThankYouTemplate[]>([]);
  const [templateId, setTemplateId] = useState('general');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [replaceTemplate, setReplaceTemplate] = useState<ThankYouTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<ThankYouTemplate | null>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [previewId, setPreviewId] = useState('');
  const [batch, setBatch] = useState<EmailBatch | null>(null);
  const [batchError, setBatchError] = useState('');
  const [recent, setRecent] = useState<Array<{ id: string; created_at: string }>>([]);
  const [recentId, setRecentId] = useState('');
  const [sendError, setSendError] = useState('');
  const pendingRequest = useRef('');
  const draftSnapshot = useRef<EmailDraft | null>(null);
  const pendingAction = useRef(false);
  const completeDraft = !!subject.trim() && !!body.trim();
  const selectedTemplate = templates.find(item => item.id === templateId);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getThankYouTemplates(), getRecentThankYouBatches()]).then(([templateResult, recentResult]) => {
      if (cancelled) return;
      if (templateResult.status === 'fulfilled') setTemplates(templateResult.value);
      else setLoadError('模板加载失败，请关闭后重新打开；仍可手动填写邮件。');
      if (recentResult.status === 'fulfilled') setRecent(recentResult.value);
      setLoadingTemplates(false);
    });
    return () => { cancelled = true; };
  }, []);

  const batchId = batch?.id;
  const finished = batch?.finished;
  useEffect(() => {
    if (!batchId || finished || step !== 'result') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const value = await getThankYouBatch(batchId);
        if (cancelled) return;
        setBatch(value);
        setBatchError('');
        if (!value.finished) timer = setTimeout(poll, 1500);
      } catch {
        if (!cancelled) {
          setBatchError('暂时无法查询发送进度，正在重试。请勿重复提交，可稍后从最近发送记录查看。');
          timer = setTimeout(poll, 5000);
        }
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [batchId, finished, step]);

  const applyTemplate = (template: ThankYouTemplate) => {
    setSubject(template.subject);
    setBody(template.body);
    setReplaceTemplate(null);
    showToast(`已导入「${template.name}」，可继续修改全部内容`);
  };

  const importTemplate = () => {
    const template = templates.find(item => item.id === templateId);
    if (!template) return;
    if ((subject.trim() || body.trim()) && (subject !== template.subject || body !== template.body)) setReplaceTemplate(template);
    else applyTemplate(template);
  };

  const saveTemplate = async () => {
    if (pendingAction.current || !completeDraft || !templateName.trim()) return;
    pendingAction.current = true;
    setBusy(true);
    try {
      const saved = await saveThankYouTemplate(templateName.trim(), subject, body);
      setTemplates(current => [...current, saved]);
      setTemplateId(saved.id);
      setSavingName(false);
      setTemplateName('');
      showToast(`模板「${saved.name}」已保存，可在导入模板中选择`);
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); pendingAction.current = false; }
  };

  const deleteTemplate = async () => {
    if (!deletingTemplate || pendingAction.current) return;
    pendingAction.current = true;
    setBusy(true);
    try {
      await deleteThankYouTemplate(deletingTemplate.id);
      setTemplates(current => current.filter(item => item.id !== deletingTemplate.id));
      setTemplateId('general');
      setDeletingTemplate(null);
      showToast(`模板「${deletingTemplate.name}」已删除`);
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); pendingAction.current = false; }
  };

  const openPreview = async () => {
    if (pendingAction.current || !completeDraft || recipientIds.length === 0) return;
    pendingAction.current = true;
    setBusy(true);
    try {
      const draft: EmailDraft = { subject, body, candidate_ids: recipientIds.map(Number), resend };
      const value = await previewThankYouEmail(draft);
      draftSnapshot.current = draft;
      pendingRequest.current = requestId();
      setPreview(value);
      setPreviewId(String((value.items.find(item => item.eligible) || value.items[0]).candidate_id));
      setSendError('');
      setStep('preview');
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); pendingAction.current = false; }
  };

  const send = async () => {
    if (!preview || !draftSnapshot.current || pendingAction.current) return;
    pendingAction.current = true;
    setBusy(true);
    setSendError('');
    try {
      const result = await sendThankYouEmail(draftSnapshot.current, preview.preview_token, pendingRequest.current);
      setBatch(result);
      setStep('result');
    } catch (error) {
      setSendError(errorMessage(error) + '。可重试本次确认，或返回编辑后重新预览。');
    } finally { setBusy(false); pendingAction.current = false; }
  };

  const openHistory = async () => {
    setBusy(true);
    try { setBatch(await getThankYouBatch(recentId)); setStep('result'); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  const previewItem = preview?.items.find(item => String(item.candidate_id) === previewId);

  return (
    <>
      <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle>{step === 'edit' ? '批量邮件' : step === 'preview' ? '确认邮件内容与收件人' : '邮件发送结果'}</DialogTitle>
            <DialogDescription>
              {step === 'edit' ? '填写邮件，或在下方导入模板。所有文字均可修改，预览确认后才会发送。' :
                step === 'preview' ? '每位候选人将单独收到一封邮件，请核对内容与收件地址。' : '邮件发送记录会保留，可在下次打开弹窗时查看。'}
            </DialogDescription>
          </DialogHeader>

          {step === 'edit' && <div className="space-y-4 min-h-0 overflow-y-auto pr-1">
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">收件候选人（{recipientIds.length} 人）</div>
              <MultiSelectCombobox label="收件候选人" options={candidates.map(row => ({
                value: String(row.id), label: `${row.name || '未命名候选人'} · ${row.job_title || '未填写岗位'} · ${row.email || '未填写邮箱'} · ${row.candidate_number || row.id}`,
              }))} value={recipientIds} onChange={setRecipientIds} placeholder="搜索姓名、岗位或邮箱..."
                renderItem={option => <span className="break-all">{option.label}</span>}
                renderSelectedItem={values => <span>已选择 {values.length} 人</span>} />
              <p className="text-xs text-slate-500">同一岗位、同一邮箱已发送的默认跳过；本次重复选择的地址仅发送一封。</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="thank-you-subject" className="text-sm font-medium text-slate-700">邮件主题 <span className="text-red-500">*</span></label>
              <Input id="thank-you-subject" value={subject} onChange={event => setSubject(event.target.value)} maxLength={200} placeholder="请输入邮件主题，或导入下方模板" />
            </div>
            <div className="space-y-2">
              <label htmlFor="thank-you-body" className="text-sm font-medium text-slate-700">邮件内容 <span className="text-red-500">*</span></label>
              <textarea id="thank-you-body" value={body} onChange={event => setBody(event.target.value)} maxLength={12000}
                className="w-full min-h-[280px] rounded-md border border-slate-300 px-3 py-2 text-sm leading-7 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="可手动填写称呼、正文、署名及页脚，也可点击下方「导入模板」后修改。" />
              <div className="flex justify-between gap-3 text-xs text-slate-500">
                <span>可使用 {'{候选人姓名}'}、{'{岗位名称}'}，发送时逐人替换。</span><span>{body.length}/12000</span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-[180px] flex-1">
                  <SearchableSelect options={templates.map(item => ({ value: item.id, label: item.name }))} value={templateId} onValueChange={setTemplateId}
                    placeholder={loadingTemplates ? '正在加载模板...' : '选择邮件模板'} searchPlaceholder="搜索模板名称..." emptyText="没有匹配的模板" />
                </div>
                <Button variant="outline" onClick={importTemplate} disabled={busy || !templates.some(item => item.id === templateId)}><FileDown className="mr-1.5 h-4 w-4" />导入模板</Button>
                <Button variant="outline" onClick={() => setSavingName(true)} disabled={busy || !completeDraft}><Save className="mr-1.5 h-4 w-4" />另存为新模板</Button>
                {selectedTemplate && !selectedTemplate.is_builtin && <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setDeletingTemplate(selectedTemplate)} disabled={busy}><Trash2 className="mr-1.5 h-4 w-4" />删除模板</Button>}
              </div>
              {loadError && <p role="alert" className="text-xs text-amber-700">{loadError}</p>}
              {savingName && <div className="space-y-2 border-t border-slate-200 pt-3">
                <label htmlFor="thank-you-template-name" className="text-sm text-slate-700">新模板名称</label>
                <div className="flex gap-2">
                  <Input id="thank-you-template-name" value={templateName} onChange={event => setTemplateName(event.target.value)} maxLength={80} placeholder="例如：面试结果通知" />
                  <Button onClick={saveTemplate} disabled={busy || !templateName.trim()}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}保存模板</Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setSavingName(false)}>取消</Button>
                </div>
                <p className="text-xs text-slate-500">保存当前主题及全部正文，供 HR 后续搜索导入；不会覆盖原模板。</p>
              </div>}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={resend} onChange={event => setResend(event.target.checked)} className="rounded border-slate-300" />允许向同一岗位下已成功发送的邮箱再次发送
            </label>
            {recent.length > 0 && <div className="flex flex-wrap gap-2 items-center border-t pt-3">
              <div className="min-w-[220px] flex-1"><SearchableSelect options={recent.map(item => ({ value: item.id, label: `${item.created_at.replace('T', ' ').slice(0, 19)} · ${item.id.slice(0, 8)}` }))}
                value={recentId} onValueChange={setRecentId} placeholder="最近发送记录" searchPlaceholder="搜索发送时间..." /></div>
              <Button variant="outline" onClick={openHistory} disabled={busy || !recentId}>查看记录</Button>
            </div>}
          </div>}

          {step === 'preview' && preview && <div className="space-y-4 min-h-0 overflow-y-auto pr-1">
            <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">可发送 {preview.eligible_count} 人，跳过 {preview.skipped_count} 人</div>
            {!preview.sending_enabled && <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">当前环境已关闭邮件发送，可编辑、保存模板及预览。启用邮件发送后才能确认投递。</p>}
            <div className="max-h-48 overflow-auto rounded-md border">
              <table className="w-full text-left text-xs"><thead className="bg-slate-50"><tr><th className="p-2">候选人 / 岗位</th><th className="p-2">邮箱</th><th className="p-2">状态</th></tr></thead>
                <tbody>{preview.items.map(item => <tr key={item.candidate_id} className="border-t align-top">
                  <td className="p-2">{item.candidate_name}<div className="text-slate-500">{item.job_title} · {item.current_stage}</div></td>
                  <td className="p-2 break-all">{item.email || '未填写'}</td>
                  <td className={`p-2 ${item.eligible ? 'text-green-700' : 'text-amber-700'}`}>{item.eligible ? '将发送' : item.reason}{item.last_sent_at && <div className="text-slate-500">上次发送：{item.last_sent_at.replace('T', ' ').slice(0, 19)}</div>}</td>
                </tr>)}</tbody></table>
            </div>
            <SearchableSelect options={preview.items.map(item => ({ value: String(item.candidate_id), label: `${item.candidate_name} · ${item.email || '无邮箱'}` }))} value={previewId} onValueChange={setPreviewId} searchPlaceholder="搜索候选人预览..." />
            {previewItem && <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="border-b pb-3 text-sm font-medium break-words">主题：{previewItem.subject}</div>
              <div className="mt-3 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">{previewItem.body}</div>
            </div>}
            {sendError && <p role="alert" className="text-sm text-red-600">{sendError}</p>}
          </div>}

          {step === 'result' && batch && <div className="space-y-3 min-h-0 overflow-y-auto pr-1">
            <div role="status" className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-medium flex items-center gap-2">{!batch.finished && <Loader2 className="h-4 w-4 animate-spin" />}{batch.finished ? '本批次处理完成' : '正在逐封发送，可关闭弹窗后查看记录'}</div>
              <div className="mt-1">已发送 {batch.counts.sent || 0} 人 · 失败 {batch.counts.failed || 0} 人 · 跳过 {batch.counts.skipped || 0} 人 · 待核实 {batch.counts.unknown || 0} 人</div>
            </div>
            {batchError && <p role="alert" className="text-sm text-amber-700">{batchError}</p>}
            <div className="max-h-[45vh] overflow-auto rounded-md border"><table className="w-full text-left text-sm">
              <thead className="bg-slate-50"><tr><th className="p-2">候选人</th><th className="p-2">邮箱</th><th className="p-2">结果</th></tr></thead>
              <tbody>{batch.items.map(item => <tr key={item.candidate_id} className="border-t align-top"><td className="p-2">{item.candidate_name}</td><td className="p-2 break-all">{item.email || '未填写'}</td>
                <td className="p-2"><span className={item.status === 'sent' ? 'text-green-700' : 'text-slate-700'}>{statusLabels[item.status] || item.status}</span>
                  {item.reason && <div className="text-xs text-amber-700 mt-1">{item.reason}</div>}{item.sent_at && <div className="text-xs text-slate-500">{item.sent_at.replace('T', ' ').slice(0, 19)}</div>}</td></tr>)}</tbody>
            </table></div>
            <p className="text-xs text-slate-500">已发送表示邮件服务已接收，不代表对方已读。失败项可重新勾选发送，结果待核实项请先核查。</p>
          </div>}

          <DialogFooter className="shrink-0">
            {step === 'edit' && <><Button variant="outline" onClick={onClose} disabled={busy}>取消</Button><Button onClick={openPreview} disabled={busy || !completeDraft || !recipientIds.length}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}预览并继续</Button></>}
            {step === 'preview' && <><Button variant="outline" onClick={() => setStep('edit')} disabled={busy}>返回编辑</Button><Button onClick={send} disabled={busy || !preview?.eligible_count || !preview?.sending_enabled}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}确认发送（{preview?.eligible_count || 0} 人）</Button></>}
            {step === 'result' && <><Button variant="outline" onClick={() => setStep('edit')}>返回编辑</Button><Button onClick={onClose}>关闭</Button></>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!replaceTemplate} onOpenChange={open => { if (!open) setReplaceTemplate(null); }}>
        <DialogContent><DialogHeader><DialogTitle>导入模板</DialogTitle><DialogDescription>导入「{replaceTemplate?.name}」将替换当前主题和全部正文。需要保留当前内容时，请先另存为新模板。</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setReplaceTemplate(null)}>取消</Button><Button onClick={() => { if (replaceTemplate) applyTemplate(replaceTemplate); }}>确认导入</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deletingTemplate} onOpenChange={open => { if (!open && !busy) setDeletingTemplate(null); }}>
        <DialogContent><DialogHeader><DialogTitle>删除邮件模板</DialogTitle><DialogDescription>确定删除「{deletingTemplate?.name}」？删除后所有 HR 都无法再导入此模板。当前编辑内容和历史发送记录会保留。</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" disabled={busy} onClick={() => setDeletingTemplate(null)}>取消</Button><Button variant="destructive" disabled={busy} onClick={deleteTemplate}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}确认删除</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
